/**
 * Live-research topic seeds, keyed by charge slug.
 *
 * Ported verbatim from the original monolith's `LR_TOPICS` literal. Each entry
 * bundles the caselaw/psychology search queries and U.S. Code references the
 * live-research aggregator fans out for a given charge.
 */

/** A U.S. Code reference used to seed statute lookups. */
export interface UscRef {
  title: number;
  section: string;
  cite: string;
}

/** Search-query and statute seeds for one charge slug. */
export interface LiveResearchTopic {
  label: string;
  caselaw: string[];
  psych: string[];
  usc: UscRef[];
}

export const LR_TOPICS: Record<string, LiveResearchTopic> = {
  murder: { label: 'Murder / Homicide',
    caselaw: ['jury selection homicide Massachusetts', 'death qualification juror bias', 'Batson peremptory challenge'],
    psych: ['juror decision making homicide', 'pretrial publicity juror bias murder', 'death qualification conviction proneness'],
    usc: [{ title: 18, section: '1111', cite: '18 U.S.C. § 1111 (Murder)' }] },
  drug: { label: 'Drug / Controlled Substance',
    caselaw: ['voir dire drug prosecution bias', 'jury nullification drug case', 'peremptory challenge drug case Massachusetts'],
    psych: ['juror attitudes drug offenses', 'war on drugs juror bias', 'jury nullification drug prosecution'],
    usc: [{ title: 21, section: '841', cite: '21 U.S.C. § 841 (Manufacture/Distribution)' }, { title: 21, section: '846', cite: '21 U.S.C. § 846 (Conspiracy)' }] },
  fraud: { label: 'Fraud / Larceny / Financial',
    caselaw: ['jury selection fraud white collar', 'complex financial evidence juror comprehension'],
    psych: ['juror comprehension complex financial evidence', 'white collar crime jury attitudes'],
    usc: [{ title: 18, section: '1341', cite: '18 U.S.C. § 1341 (Mail Fraud)' }, { title: 18, section: '1343', cite: '18 U.S.C. § 1343 (Wire Fraud)' }] },
  assault: { label: 'Assault & Battery',
    caselaw: ['self defense jury instruction Massachusetts', 'voir dire violence exposure juror'],
    psych: ['juror attitudes self defense claims', 'violence exposure juror bias'],
    usc: [{ title: 18, section: '113', cite: '18 U.S.C. § 113 (Assaults, federal jurisdiction)' }] },
  oui: { label: 'OUI / DUI',
    caselaw: ['jury selection OUI drunk driving', 'breathalyzer evidence juror attitudes Massachusetts'],
    psych: ['juror attitudes drunk driving MADD', 'breathalyzer reliability juror perception'],
    usc: [] },
  sexual: { label: 'Sexual Assault / Rape',
    caselaw: ['voir dire sexual assault juror bias', 'rape shield jury Massachusetts', 'first complaint doctrine Massachusetts'],
    psych: ['rape myth acceptance juror verdict', 'juror bias sexual assault cases'],
    usc: [{ title: 18, section: '2241', cite: '18 U.S.C. § 2241 (Aggravated Sexual Abuse)' }] },
};
